import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';

import { useChatStore } from '../useChatStore';
import { inferModuleFromPathname } from '../utils/inferModuleFromPathname';
import { startersToChatOptions } from './staticStarters';
import { useDynamicStarters } from './useDynamicStarters';

export const useChatOptions = (
  sendMessage: (message: string) => Promise<void>
) => {
  const {
    messages,
    sessionInitialized,
    isInitialising,
    setOptions,
    clearOptions,
  } = useChatStore();

  const { pathname } = useLocation();
  const { user } = useRisksmartUser();
  const module = inferModuleFromPathname(pathname);
  const { starters, source } = useDynamicStarters(module, user?.orgRole);

  const options = useMemo(
    () => startersToChatOptions(starters, sendMessage, source),
    [starters, sendMessage, source]
  );

  const lastSetIdsRef = useRef<string>('');

  useEffect(() => {
    const hasSystemMessage = messages.some((message) => message.isSystem);
    const shouldShowOptions =
      hasSystemMessage && sessionInitialized && !isInitialising;

    if (!hasSystemMessage) {
      if (lastSetIdsRef.current) {
        clearOptions();
        lastSetIdsRef.current = '';
      }

      return;
    }

    if (shouldShowOptions) {
      const ids = options.map((o) => o.id).join('|');
      if (ids !== lastSetIdsRef.current) {
        setOptions(options);
        lastSetIdsRef.current = ids;
      }
    }
  }, [
    messages,
    sessionInitialized,
    isInitialising,
    options,
    setOptions,
    clearOptions,
  ]);
};
